import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import type { Employee, Department, Location } from "../../types";

const DEFAULT_ROLES = [
  { id: "admin", label: "Admin" },
  { id: "manager", label: "Manager" },
  { id: "employee", label: "Employee" },
];

const EMPTY_FORM = { firstName: "", lastName: "", email: "", phone: "", jobTitle: "", departmentId: "", locationId: "", role: "employee", startDate: "" };

export default function EmployeesTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => { if (orgId) loadData(); }, [orgId]);

  async function loadData() {
    try {
      const [emps, depts, locs, config] = await Promise.all([
        apiGet<Employee[]>(`/organizations/${orgId}/employees`),
        apiGet<Department[]>(`/organizations/${orgId}/departments`),
        apiGet<Location[]>(`/organizations/${orgId}/locations`),
        apiGet<{ roles?: { id: string; label: string }[] }>(`/organizations/${orgId}/config`),
      ]);
      setEmployees(emps); setDepartments(depts); setLocations(locs);
      setRoles([...DEFAULT_ROLES, ...(config.roles || [])]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (emp: any) => {
    setEditing(emp.employeeId);
    setForm({ firstName: emp.firstName || "", lastName: emp.lastName || "", email: emp.email || "", phone: emp.phone || "", jobTitle: emp.jobTitle || "", departmentId: emp.departmentId || "", locationId: emp.locationId || "", role: emp.role || "employee", startDate: emp.startDate || "" });
    setShowModal(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      await apiPut(`/organizations/${orgId}/employees/${editing}`, form);
    } else {
      await apiPost(`/organizations/${orgId}/employees`, form);
    }
    setShowModal(false);
    await loadData();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Remove this employee?")) return;
    await apiDelete(`/organizations/${orgId}/employees/${id}`);
    await loadData();
  };

  const getDeptName = (id?: string) => departments.find((d) => d.departmentId === id)?.name || "—";
  const getLocName = (id?: string) => locations.find((l) => l.locationId === id)?.name || "—";
  const getRoleLabel = (id: string) => roles.find((r) => r.id === id)?.label || id;

  if (loading) return <div className="empty-state">Loading employees...</div>;

  return (
    <div>
      <div className="org-mgmt-header">
        <h2>Employees</h2>
        <button className="btn btn-primary" style={{ width: "auto" }} onClick={openCreate}>+ Add Employee</button>
      </div>

      {employees.length === 0 ? (
        <div className="empty-state"><p>No employees yet.</p></div>
      ) : (
        <table className="data-table">
          <thead><tr><th>Name</th><th>Job Title</th><th>Department</th><th>Location</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {employees.map((emp: any) => (
              <tr key={emp.employeeId}>
                <td><div style={{ fontWeight: 500 }}>{emp.firstName} {emp.lastName}</div><div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{emp.email}</div></td>
                <td>{emp.jobTitle || "—"}</td>
                <td>{getDeptName(emp.departmentId)}</td>
                <td>{getLocName(emp.locationId)}</td>
                <td>{getRoleLabel(emp.role)}</td>
                <td><span className={`status-badge ${emp.status}`}>{emp.status}</span></td>
                <td><div className="table-actions"><button onClick={() => openEdit(emp)}>Edit</button><button className="delete" onClick={() => handleDelete(emp.employeeId)}>Remove</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? "Edit Employee" : "Add Employee"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="settings-form">
                <div className="form-row">
                  <div className="form-group"><label>First Name</label><input name="firstName" value={form.firstName} onChange={handleChange} required /></div>
                  <div className="form-group"><label>Last Name</label><input name="lastName" value={form.lastName} onChange={handleChange} required /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Email</label><input name="email" type="email" value={form.email} onChange={handleChange} required /></div>
                  <div className="form-group"><label>Phone</label><input name="phone" value={form.phone} onChange={handleChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Job Title</label><input name="jobTitle" value={form.jobTitle} onChange={handleChange} /></div>
                  <div className="form-group"><label>Role</label><select name="role" value={form.role} onChange={handleChange}>{roles.map((r) => (<option key={r.id} value={r.id}>{r.label}</option>))}</select></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Department</label><select name="departmentId" value={form.departmentId} onChange={handleChange}><option value="">None</option>{departments.map((d: any) => (<option key={d.departmentId} value={d.departmentId}>{d.name}</option>))}</select></div>
                  <div className="form-group"><label>Location</label><select name="locationId" value={form.locationId} onChange={handleChange}><option value="">None</option>{locations.map((l: any) => (<option key={l.locationId} value={l.locationId}>{l.name}</option>))}</select></div>
                </div>
                <div className="form-group"><label>Start Date</label><input name="startDate" type="date" value={form.startDate} onChange={handleChange} /></div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ width: "auto" }}>{editing ? "Save Changes" : "Add Employee"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
